import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

type FileUploadType = 'box' | 'button';

@Component({
  selector: 'lib-cdw-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: false,
})
export class CdwFileUploadComponent {
  @Input() type: FileUploadType = 'box';
  @Input() accept = '.csv';
  @Input() multiple = false;
  @Input() disabled = false;
  @Input() dragMessage = 'Drag your file into this box';
  @Input() buttonText = 'Select file';
  @Input() buttonLevel: 'primary' | 'secondary' = 'primary';
  @Input() showIcon = true;
  @Input() iconName = 'upload_file';

  // Drag message styling
  @Input() dragMessageFontFamily =
    "'Source Sans 3', Arial, Helvetica, sans-serif";
  @Input() dragMessageFontSize = '1rem';
  @Input() dragMessageFontWeight = '500';
  @Input() dragMessageColor = '#9ca3af';

  // Selected file text styling (for both box and button types)
  @Input() selectedFileTextFontFamily = 'Roboto, Arial, Helvetica, sans-serif';
  @Input() selectedFileTextFontSize = '0.875rem';
  @Input() selectedFileTextFontWeight = '400';

  // Button label styling
  @Input() buttonLabelFontFamily = 'Roboto';
  @Input() buttonLabelFontSize = '14px';
  @Input() buttonLabelFontWeight = '700';
  @Input() buttonLabelColor?: string;

  // Box styling
  @Input() boxBackgroundColor = '#f9fafb';
  @Input() boxBorderColor = '#d1d5db';

  // Upload state
  @Input() isUploading = false;
  @Input() uploadingText = 'Uploading...';
  @Input() uploadingTextColor = '#7B818C';

  // Pre-selected file
  @Input() set selectedFile(file: File | null) {
    if (file) {
      this.selectedFiles = [file];
    } else {
      this.selectedFiles = [];
    }
  }

  @Output() fileSelected = new EventEmitter<File | File[] | null>();

  public isDragOver = false;
  public selectedFiles: File[] = [];

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(input.files);
    }
  }

  private handleFiles(files: FileList): void {
    const fileArray = Array.from(files);

    // Filter files by accept pattern if specified
    const filteredFiles = this.accept
      ? fileArray.filter((file) => this.matchesAcceptPattern(file, this.accept))
      : fileArray;

    if (filteredFiles.length === 0) return;

    if (this.multiple) {
      this.selectedFiles = filteredFiles;
      this.fileSelected.emit(filteredFiles);
    } else {
      this.selectedFiles = [filteredFiles[0]];
      this.fileSelected.emit(filteredFiles[0]);
    }
  }

  private matchesAcceptPattern(file: File, accept: string): boolean {
    const acceptPatterns = accept.split(',').map((p) => p.trim());

    return acceptPatterns.some((pattern) => {
      if (pattern.startsWith('.')) {
        // Extension match
        return file.name.toLowerCase().endsWith(pattern.toLowerCase());
      }
      if (pattern.includes('/')) {
        // MIME type match
        return (
          file.type === pattern ||
          file.type.match(new RegExp(pattern.replace(/\*/g, '.*')))
        );
      }
      return false;
    });
  }

  get displayText(): string {
    if (this.selectedFiles.length === 0) {
      return '';
    }
    if (this.selectedFiles.length === 1) {
      return this.selectedFiles[0].name;
    }
    return `${this.selectedFiles.length} files selected`;
  }

  clearSelection(): void {
    this.selectedFiles = [];
    this.fileSelected.emit(null);
  }
}
